import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";

type InputContainerA11yContextValue = {
  id: string;
  errorId: string;
  describedBy?: string;
  ariaInvalid?: React.AriaAttributes["aria-invalid"];
};

const InputContainerA11yContext =
  React.createContext<InputContainerA11yContextValue | null>(null);

export type InputContainerClassNames = {
  root?: string;
  label?: string;
  container?: string;
  error?: string;
};

export type InputContainerProps = {
  label?: string;
  required?: boolean;
  errorMessage?: string;
  /** ID for the input (auto-generated if omitted). */
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  bare?: boolean;
  classNames?: InputContainerClassNames;
  children: React.ReactNode;
  className?: string;
};

export const INPUT_CONTAINER_INPUT_CLASSNAME =
  "w-full h-full bg-transparent px-[11px] text-[14px] font-[400] text-text-secondary placeholder:text-secondary-light border-0 border-transparent outline-none ring-0 shadow-none focus:border-0 focus:border-transparent focus:outline-none focus:outline-transparent focus:ring-0 focus:ring-offset-0 focus:shadow-none focus-visible:border-0 focus-visible:border-transparent focus-visible:outline-none focus-visible:outline-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-none";

export type InputContainerInputProps = React.ComponentPropsWithoutRef<"input">;

export const InputContainerInput = React.forwardRef<
  HTMLInputElement,
  InputContainerInputProps
>(
  (
    {
      className,
      id: idProp,
      "aria-describedby": ariaDescribedByProp,
      "aria-invalid": ariaInvalidProp,
      ...props
    },
    ref,
  ) => {
    const ctx = React.useContext(InputContainerA11yContext);
    return (
      <input
        {...props}
        ref={ref}
        id={idProp ?? ctx?.id}
        aria-describedby={twMerge(ctx?.describedBy, ariaDescribedByProp)}
        aria-invalid={ctx?.ariaInvalid ?? ariaInvalidProp}
        className={twMerge(INPUT_CONTAINER_INPUT_CLASSNAME, className)}
      />
    );
  },
);

InputContainerInput.displayName = "InputContainerInput";

export type InputContainerTextareaProps =
  React.ComponentPropsWithoutRef<"textarea">;

export const InputContainerTextarea = React.forwardRef<
  HTMLTextAreaElement,
  InputContainerTextareaProps
>(
  (
    {
      className,
      id: idProp,
      "aria-describedby": ariaDescribedByProp,
      "aria-invalid": ariaInvalidProp,
      ...props
    },
    ref,
  ) => {
    const ctx = React.useContext(InputContainerA11yContext);
    return (
      <textarea
        {...props}
        ref={ref}
        id={idProp ?? ctx?.id}
        aria-describedby={twMerge(ctx?.describedBy, ariaDescribedByProp)}
        aria-invalid={ctx?.ariaInvalid ?? ariaInvalidProp}
        className={twMerge(
          INPUT_CONTAINER_INPUT_CLASSNAME,
          "resize-none",
          className,
        )}
      />
    );
  },
);

InputContainerTextarea.displayName = "InputContainerTextarea";

function InputContainer({
  label,
  required,
  errorMessage,
  id: idProp,
  "aria-describedby": ariaDescribedByProp,
  "aria-invalid": ariaInvalidProp,
  bare,
  classNames,
  children,
  className,
}: InputContainerProps) {
  const autoId = React.useId();
  const id = idProp ?? autoId;
  const errorId = `${id}-error`;
  const describedBy =
    [errorMessage ? errorId : null, ariaDescribedByProp]
      .filter(Boolean)
      .join(" ") || undefined;

  const ctxValue = React.useMemo<InputContainerA11yContextValue>(
    () => ({
      id,
      errorId,
      describedBy,
      ariaInvalid: errorMessage ? true : ariaInvalidProp,
    }),
    [id, errorId, describedBy, errorMessage, ariaInvalidProp],
  );

  return (
    <div className={twMerge("grid w-full", className)}>
      {label ? (
        <label htmlFor={id}>
          <Text className={twMerge("mb-[8px]", classNames?.label)}>
            {required ? (
              <Text className="text-text-critical" as="span" children="* " />
            ) : null}
            {label}
          </Text>
        </label>
      ) : null}

      <InputContainerA11yContext.Provider value={ctxValue}>
        {bare ? (
          children
        ) : (
          <div
            children={children}
            className={twMerge(
              "w-full h-[32px] rounded-[2px] border bg-surface flex items-center gap-2 transition focus-within:ring-0 focus-within:shadow-none focus-within:border-input-border",
              errorMessage
                ? "border-critical focus-within:border-critical"
                : "border-input-border",
              classNames?.container,
            )}
          />
        )}
      </InputContainerA11yContext.Provider>

      {errorMessage ? (
        <Text
          level={5}
          children={errorMessage}
          className={twMerge("mt-[4px] text-critical", classNames?.error)}
        />
      ) : null}
    </div>
  );
}

InputContainer.Input = InputContainerInput;
InputContainer.Textarea = InputContainerTextarea;

export default InputContainer;
